import * as React from 'react';
import { domToReact, htmlToDOM } from 'html-react-parser';
import {useEffect, useState} from 'react';
import {useIntersect} from '../../../../Utilities/UseIntersect';
import uniqueId from 'lodash/uniqueId';


export interface IHeaderProps {
    /* DOM to parse */
    dom: string;
    /** Unsafe config */
    config?: any;
    componentMapper: (maybeComponent: string, maybeContent: string, maybeConfig) => React.Component;
}

const Header: React.FunctionComponent<IHeaderProps> = (props) => {
    const domTree = htmlToDOM(props.dom.replace(/>\s+</g, '><'));

    const [isDesktopSticky, setIsDesktopSticky] = useState(true);
    const [isMobileSticky, setIsMobileSticky] = useState(false);
    const [menuVisible, setMenuVisible] = useState(false);
    const [id, setId] = React.useState<string>(uniqueId());

    const preDesktop = useIntersect({
        root: null,
        rootMargin: '0px',
        threshold: Array.from(Array(100)
            .keys(), (i) => i / 100),
    });

    // Watch top intersection

    useEffect(() => {
        if (preDesktop.entry) {
            const el = document.getElementById('stickyHeader');
            const doc = document.documentElement;
            if (preDesktop.entry.intersectionRatio > 0) {
                if(el) {
                    setIsDesktopSticky(false);
                    el.style.paddingBottom = '0px';
                    if (doc) {
                        doc.classList.remove('is-sticky');
                    }
                }
            } else {
                if(el) {
                    setIsDesktopSticky(true);
                    el.style.paddingBottom = getMobileHeaderHeight() + 'px';
                    if (doc) {
                        doc.classList.add('is-sticky');
                    }
                }
            }
        }
    }, [preDesktop.entry]);

    useEffect(() => {
        // Handler to call on window resize
        function handleResize () {
            const el = document.getElementById('stickyHeader');
            if(el) {
                el.style.top = (1) + 'px';
            }
            if(isDesktopSticky){
                if(el) {
                    el.style.paddingBottom = getMobileHeaderHeight() + 'px';
                }
            }
        }
        // Add event listener
        window.addEventListener('resize', handleResize);
        // Call handler right away so state gets updated with initial window size
        handleResize();
        // Remove event listener on cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []); // Empty array ensures that effect is only run on mount

    // Handle scrolling up
    const [scrollPosition, setScrollPosition] = useState(window.pageYOffset);
    const handleScroll = () => {
        if (props.config?.revealMenu === true) {
            const position = window.pageYOffset;
            if (position < scrollPosition) {
                setMenuVisible(true);
            } else {
                setMenuVisible(false);
            }
            setScrollPosition(position);
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    useEffect(() => {
        window.addEventListener('keydown', handleFirstTab);
        return function cleanup () {
            window.removeEventListener('keydown', handleFirstTab);
        };
    });

    const handleFirstTab = (e) => {
        if (e.keyCode === 9) {
            document.body.classList.add('visible-focus');
            window.removeEventListener('keydown', handleFirstTab);
        }
    };

    const getMobileHeaderHeight = () => {
        let height = 0;
        const el = document.getElementsByClassName('header__inner')[0];
        if(el) {
            const rect = el.getBoundingClientRect();
            height += rect.height;
        }
        return height;
    };

    const parserOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if (domNode.name === 'div' && domNode.attribs.class === 'frame-ebeauty__header__nav') {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, ...rest } = domNode.attribs;

                    return (
                        <React.Fragment>
                            <div className={className} {...rest}>
                                {inner}
                            </div>
                        </React.Fragment>
                    );
                }

                if (domNode.name === 'div' && domNode.attribs.id === 'main-header') {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className } = domNode.attribs;

                    return (
                        <div id="main-header">
                            <div style={{height:0 + 'px', position: 'relative', top: 0}} ref={preDesktop.node} />
                            <div style={{top: 0}} className={['header__inner', isDesktopSticky ? 'is-sticky' : undefined, isMobileSticky ? 'is-mobile-sticky' : undefined, menuVisible ? 'has-menu' : undefined].join(' ')}>
                                {inner}
                            </div>
                        </div>
                    );
                }

                if (domNode.attribs['data-subcomponent']){
                    const maybeConfig = JSON.parse(domNode.attribs['data-config'] || '{}');
                    const maybeComponent = domNode.attribs['data-subcomponent'];
                    const maybeContent = domNode.children ?? '';
                    const { class: className, ...rest } = domNode.attribs;
                    const mappedComponent = props.componentMapper(maybeComponent, maybeContent, maybeConfig);

                    if (mappedComponent) {
                        return (
                            <domNode.name className={className} {...rest}>
                                {mappedComponent}
                            </domNode.name>
                        );
                    }
                }

                if (domNode.attribs['data-component']){
                    const maybeConfig = JSON.parse(domNode.attribs['data-config'] || '{}');
                    const maybeComponent = domNode.attribs['data-component'];
                    const maybeContent = domNode.children ?? '';
                    const { class: className, ...rest } = domNode.attribs;
                    const mappedComponent = props.componentMapper(maybeComponent, maybeContent, maybeConfig);

                    if (mappedComponent) {
                        return (
                            <domNode.name className={className} {...rest}>
                                {mappedComponent}
                            </domNode.name>
                        );
                    }
                }

                if (domNode.attribs.class==='menu-toggle'){
                    const { class: className } = domNode.attribs;
                    const inner = domToReact(domNode.children, parserOptions);
                    return (
                        <button className={'menu-toggle'} onClick={() => setMenuVisible(!menuVisible)}>
                            {inner}
                        </button>
                    );
                }
            }
            return false;
        },
    };
    return (
        <React.Fragment>
            {domToReact(domTree, parserOptions)}
        </React.Fragment>
    );
};

Header.displayName = 'Header';

export default Header;
